import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { describePlanningScope } from '@platform/client';
import { planningScopeSummarySchema } from '@platform/schema/chat';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { castQuery } from '../authoring/cast';
import { exchangeCost } from '../authoring/chat';
import { JobVerification } from '../authoring/Verification';
import { counted } from '../library/counted';

import { useAskAi } from './AskAiProvider';
import { READINESS_LABEL } from './cast';
import { linked } from './chat';
import { PLANNING_TEXT } from './LessonDrawer';
import { assignmentName, castLine } from './ModuleCast';

import type { PanelMode } from './AskAiProvider';
import type { Outcome } from './chat';
import type {
  CurriculumAnswer,
  PlanningCastProposal,
  PlanningProposal,
  PlanningScope,
  PlanningScopeSummary,
  PlanningStoryProposal,
} from '@platform/schema/chat';
import type { ReactNode } from 'react';

export type ScopeKind = PlanningScope['kind'];

/** One way to scope a question, and why it cannot be picked right now. */
export interface ScopeOption {
  kind: ScopeKind;
  label: string;
  /** `null` when the scope has lessons in it. */
  unavailable: string | null;
}

/** Narrow enough at 1280 px to leave the table its filters and actions — CURR-30. */
const PANEL_WIDTH = 'clamp(20rem, 26vw, 27.5rem)';
const RAIL_WIDTH = '6rem';

/** How wide the panel is in each mode, so the detailed view can stop short of it. */
export const panelWidth = (mode: PanelMode): string | undefined =>
  mode === 'open' ? PANEL_WIDTH : mode === 'collapsed' ? RAIL_WIDTH : undefined;
// Named because the raw-pixel lint reads a bare `gap: 1` as pixels; it is MUI spacing.
const GAP = 1;

const KIND_LABEL: Record<ScopeKind, string> = {
  lesson: 'Current lesson',
  selected: 'Selected lessons',
  filter: 'Filtered lessons',
  curriculum: 'Entire curriculum',
};

const REFUSED: Record<'stale' | 'missing', string> = {
  stale: 'Changed since proposed',
  missing: 'No longer in the plan',
};

const shown = (value: string | undefined): string =>
  value === undefined || value === '' ? '—' : value;

const fieldLabel = (key: string): string =>
  PLANNING_TEXT.find((one) => one.key === key)?.label ?? key;

/** Where the scope's lessons sit, as briefly as the count allows. */
function placeOf(courses: PlanningScopeSummary['courses']): string {
  const [only] = courses;
  if (only === undefined) return 'No lesson is in this scope.';
  if (courses.length === 1)
    return only.modules.length === 1
      ? `${only.name} › ${only.modules.join('')}`
      : `${only.name} › ${counted(only.modules.length, 'module')}`;
  const modules = courses.reduce((n, one) => n + one.modules.length, 0);
  return `${counted(courses.length, 'course')}, ${counted(modules, 'module')}`;
}

/** The panel's scope as the API reads it; shared by the panel and the switch offer. */
function useScopeSummary(enabled: boolean) {
  const { scope } = useAskAi();
  return useQuery({
    queryKey: ['curriculum', 'scope', scope],
    enabled,
    queryFn: async () =>
      planningScopeSummarySchema.parse(
        (await describePlanningScope({ body: { scope }, throwOnError: true }))
          .data,
      ),
  });
}

/**
 * Offered when a row click would move a conversation to another lesson: the scope changes
 * only on Switch. Shown in the panel and in the detailed view the click opened.
 */
export function ScopeSwitch({ lessonId }: { lessonId: string }) {
  const ai = useAskAi();
  const about = useScopeSummary(true).data?.label ?? 'its current scope';
  return (
    <Alert
      severity="info"
      aria-label="Change the AI scope?"
      action={
        <Stack spacing={0.5}>
          <Button
            size="small"
            color="inherit"
            onClick={() => {
              ai.setScope({ kind: 'lesson', lessonId });
            }}
          >
            Switch
          </Button>
          <Button
            size="small"
            color="inherit"
            onClick={() => {
              ai.propose(null);
            }}
          >
            Keep
          </Button>
        </Stack>
      }
    >
      {ai.asking ? 'Ask AI is still answering about' : 'Ask AI is about'}{' '}
      {about}. Switch it to lesson {lessonId}?
    </Alert>
  );
}

/**
 * The AI panel beside the Curriculum table — CURR-25, made to fit and to keep its place
 * by CURR-30. It reads planning data and proposes edits; nothing is written until the
 * admin applies one. Its state is `AskAiProvider`'s, so collapsing it, closing it or
 * leaving the page loses nothing; a reload starts afresh.
 */
export function CurriculumChat({
  scopes,
  onPick,
  onOpenLesson,
  onBuild,
  onVerify,
}: {
  scopes: readonly ScopeOption[];
  onPick: (kind: ScopeKind) => void;
  onOpenLesson: (lessonId: string) => void;
  /** Build the scope's lessons, verifying them too when asked — CURR-31. */
  onBuild: (verify: boolean) => void;
  onVerify: () => void;
}) {
  const ai = useAskAi();
  const box = useRef<HTMLTextAreaElement>(null);
  const log = useRef<HTMLDivElement>(null);
  const [listing, setListing] = useState(false);
  const [question, setQuestion] = useState('');
  const open = ai.mode === 'open';

  useEffect(() => {
    if (ai.instructing > 0) box.current?.focus();
  }, [ai.instructing]);
  useEffect(() => {
    const pane = log.current;
    if (pane !== null) pane.scrollTop = pane.scrollHeight;
  }, [ai.said.length, open]);

  const summary = useScopeSummary(open);

  if (ai.mode === 'closed') return null;

  const controls = (
    <Button
      size="small"
      aria-label="Close the AI panel"
      onClick={() => {
        ai.setMode('closed');
      }}
    >
      Close
    </Button>
  );

  if (ai.mode === 'collapsed')
    return (
      <Paper
        variant="outlined"
        component="aside"
        aria-label="AI panel"
        sx={{
          width: panelWidth(ai.mode),
          flexShrink: 0,
          p: 1,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: GAP,
        }}
      >
        <Button
          size="small"
          variant="outlined"
          aria-label="Expand the AI panel"
          onClick={() => {
            ai.setMode('open');
          }}
        >
          Expand
        </Button>
        <Typography variant="subtitle2" sx={{ writingMode: 'vertical-rl' }}>
          Ask AI
        </Typography>
        {ai.asking && (
          <Typography variant="caption" role="status">
            Answering…
          </Typography>
        )}
        {controls}
      </Paper>
    );

  const described = summary.data;
  const latestJob = ai.started.at(-1);
  const lesson = ai.scope.kind === 'lesson' ? described?.lessons[0] : undefined;
  const empty = described === undefined || described.lessonCount === 0;
  const ready =
    question.trim() !== '' && !ai.asking && described?.lessonCount !== 0;

  return (
    <Paper
      variant="outlined"
      component="aside"
      aria-label="AI panel"
      sx={{
        width: panelWidth(ai.mode),
        flexShrink: 0,
        p: 2,
        maxHeight: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: GAP,
      }}
    >
      <Stack direction="row" sx={{ alignItems: 'center' }}>
        <Typography variant="h6" component="h2" sx={{ flex: 1 }}>
          Ask AI
        </Typography>
        <Button
          size="small"
          aria-label="Collapse the AI panel"
          onClick={() => {
            ai.setMode('collapsed');
          }}
        >
          Collapse
        </Button>
        {controls}
      </Stack>

      <TextField
        select
        size="small"
        label="Scope"
        value={ai.scope.kind}
        slotProps={{
          select: { renderValue: (kind) => KIND_LABEL[kind as ScopeKind] },
        }}
      >
        {scopes.map((one) => (
          <MenuItem
            key={one.kind}
            value={one.kind}
            disabled={one.unavailable !== null}
            // A click rather than the select's change, so picking the same kind again
            // takes the table's selection or filter as it is now.
            onClick={() => {
              setListing(false);
              onPick(one.kind);
            }}
          >
            <ListItemText
              primary={one.label}
              secondary={one.unavailable ?? undefined}
            />
          </MenuItem>
        ))}
      </TextField>

      <Stack
        role="status"
        aria-label="What the AI is asked about"
        spacing={0.25}
      >
        <Typography variant="subtitle2">
          {KIND_LABEL[ai.scope.kind]}
          {described === undefined
            ? ''
            : ` · ${counted(described.lessonCount, 'lesson')}`}
        </Typography>
        {ai.scope.kind === 'lesson' && (
          <Typography variant="body2">
            {ai.scope.lessonId}
            {lesson === undefined ? '' : ` · ${lesson.name}`}
          </Typography>
        )}
        {described !== undefined && (
          <Typography variant="body2" color="text.secondary">
            {placeOf(described.courses)}
          </Typography>
        )}
        <Typography variant="body2" color="text.secondary">
          Planning data, not built lessons
        </Typography>
      </Stack>
      <Stack
        direction="row"
        spacing={1}
        role="group"
        aria-label="Run on the scope"
        sx={{ flexWrap: 'wrap' }}
      >
        <Button
          size="small"
          variant="outlined"
          disabled={empty}
          onClick={() => {
            onBuild(true);
          }}
        >
          Build and verify
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={empty}
          onClick={onVerify}
        >
          Verify
        </Button>
      </Stack>
      {latestJob !== undefined && (
        <Stack spacing={0.5} role="region" aria-label="Latest job">
          <Typography variant="body2">
            Latest job:{' '}
            <Link
              component={RouterLink}
              to={`/authoring/jobs/${latestJob}`}
              variant="body2"
            >
              open it
            </Link>
          </Typography>
          <JobVerification id={latestJob} runs={false} />
        </Stack>
      )}
      {summary.isPending && <LinearProgress />}
      {described !== undefined && ai.scope.kind !== 'lesson' && (
        <>
          <Button
            size="small"
            aria-expanded={listing}
            sx={{ alignSelf: 'flex-start' }}
            onClick={() => {
              setListing((was) => !was);
            }}
          >
            {listing ? 'Hide the lessons' : 'Show the lessons'}
          </Button>
          {listing && (
            <Stack
              component="ul"
              aria-label="Lessons in scope"
              sx={{ m: 0, pl: 2, maxHeight: '30%', overflowY: 'auto' }}
            >
              {described.lessons.map((one) => (
                <Typography component="li" variant="body2" key={one.lessonId}>
                  <Link
                    component="button"
                    variant="body2"
                    onClick={() => {
                      onOpenLesson(one.lessonId);
                    }}
                  >
                    {one.lessonId}
                  </Link>{' '}
                  · {one.name}
                </Typography>
              ))}
              {described.lessonCount > described.lessons.length && (
                <Typography component="li" variant="body2">
                  and{' '}
                  {(
                    described.lessonCount - described.lessons.length
                  ).toLocaleString()}{' '}
                  more
                </Typography>
              )}
            </Stack>
          )}
        </>
      )}
      {ai.proposed !== null && <ScopeSwitch lessonId={ai.proposed} />}

      <Stack
        ref={log}
        role="log"
        aria-label="Conversation"
        spacing={1}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
      >
        {ai.said.length === 0 && (
          <Typography variant="body2" color="text.secondary">
            Ask about the planned lessons in scope, or for edits to them. Each
            edit is shown before and after, and nothing changes until you apply
            it.
          </Typography>
        )}
        {ai.said.map((one, at) => (
          <Paper
            // Turns are only appended, so the position is stable.
            key={at}
            variant="outlined"
            sx={{ p: 1 }}
          >
            {'question' in one ? (
              <>
                <Typography variant="caption" color="text.secondary">
                  You
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {one.question}
                </Typography>
              </>
            ) : (
              <Answer
                answer={one.answer}
                settled={one.settled ?? {}}
                story={one.story}
                cast={one.cast}
                busy={ai.applying}
                onOpenLesson={onOpenLesson}
                onApply={(which) => {
                  ai.apply(at, which);
                }}
                onDismiss={(which) => {
                  ai.dismiss(at, which);
                }}
                onAcceptStory={() => {
                  ai.acceptStory(at);
                }}
                onDismissStory={() => {
                  ai.dismissStory(at);
                }}
                onAcceptCast={() => {
                  ai.acceptCast(at);
                }}
                onDismissCast={() => {
                  ai.dismissCast(at);
                }}
              />
            )}
          </Paper>
        ))}
      </Stack>

      {ai.asking && <LinearProgress />}

      <TextField
        inputRef={box}
        label={ai.instructing > 0 ? 'What should change?' : 'Your question'}
        placeholder={
          ai.instructing > 0
            ? 'For example: shorten each description to one sentence'
            : undefined
        }
        value={question}
        size="small"
        multiline
        maxRows={6}
        onChange={(event) => {
          setQuestion(event.target.value);
        }}
      />
      <Button
        variant="contained"
        disabled={!ready}
        sx={{ alignSelf: 'flex-start' }}
        onClick={() => {
          ai.ask(question.trim(), setQuestion);
          setQuestion('');
        }}
      >
        Ask
      </Button>
      <Typography variant="caption" color="text.secondary">
        Kept while you move around the admin. Reloading the page clears this
        conversation.
      </Typography>
    </Paper>
  );
}

function Answer({
  answer,
  settled,
  story,
  cast,
  busy,
  onOpenLesson,
  onApply,
  onDismiss,
  onAcceptStory,
  onDismissStory,
  onAcceptCast,
  onDismissCast,
}: {
  answer: CurriculumAnswer;
  settled: Record<number, Outcome>;
  story: Outcome | undefined;
  cast: Outcome | undefined;
  busy: boolean;
  onOpenLesson: (lessonId: string) => void;
  onApply: (which: number[]) => void;
  onDismiss: (which: number) => void;
  onAcceptStory: () => void;
  onDismissStory: () => void;
  onAcceptCast: () => void;
  onDismissCast: () => void;
}) {
  const lessonLink = (lessonId: string) => (
    <Link
      component="button"
      variant="body2"
      onClick={() => {
        onOpenLesson(lessonId);
      }}
    >
      {lessonId}
    </Link>
  );
  const open = answer.proposals.flatMap((_, n) =>
    settled[n] === undefined ? [n] : [],
  );

  return (
    <Stack spacing={1}>
      <Typography variant="caption" color="text.secondary">
        AI · about {answer.scope.label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
        {linked(answer.text, answer.lessonIds).map((part, n) =>
          part.lessonId === undefined ? (
            part.text
          ) : (
            // Segments of a settled answer never change, so the position is stable.
            <span key={n}>{lessonLink(part.lessonId)}</span>
          ),
        )}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        {exchangeCost(answer)}
      </Typography>

      {answer.storyProposals.map((proposal) => (
        <StoryProposal
          key={proposal.moduleId}
          proposal={proposal}
          outcome={story}
          busy={busy}
          link={lessonLink}
          onAccept={onAcceptStory}
          onDismiss={onDismissStory}
        />
      ))}

      {answer.castProposals.map((proposal) => (
        <CastProposal
          key={proposal.moduleId}
          proposal={proposal}
          outcome={cast}
          busy={busy}
          link={lessonLink}
          onAccept={onAcceptCast}
          onDismiss={onDismissCast}
        />
      ))}

      {answer.proposals.length > 0 && (
        <Stack spacing={1} role="list" aria-label="Proposals">
          {answer.proposals.map((proposal, n) => (
            <Proposal
              // A settled answer's proposals are a fixed list.
              key={n}
              proposal={proposal}
              outcome={settled[n]}
              busy={busy}
              link={lessonLink(proposal.lessonId)}
              onApply={() => {
                onApply([n]);
              }}
              onDismiss={() => {
                onDismiss(n);
              }}
            />
          ))}
          {answer.proposals.length > 1 && (
            <Button
              size="small"
              variant="outlined"
              disabled={busy || open.length === 0}
              sx={{ alignSelf: 'flex-start' }}
              onClick={() => {
                onApply(open);
              }}
            >
              Apply all ({open.length})
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  );
}

function Proposal({
  proposal,
  outcome,
  busy,
  link,
  onApply,
  onDismiss,
}: {
  proposal: PlanningProposal;
  outcome: Outcome | undefined;
  busy: boolean;
  link: ReactNode;
  onApply: () => void;
  onDismiss: () => void;
}) {
  const fields = Object.keys(proposal.after) as (keyof typeof proposal.after)[];
  return (
    <Paper
      variant="outlined"
      role="listitem"
      aria-label={`Proposal for ${proposal.lessonId}`}
      sx={{ p: 1 }}
    >
      <Stack spacing={0.5}>
        <Typography variant="subtitle2">Edit {link}</Typography>
        {fields.map((field) => (
          <Stack key={field} spacing={0.25}>
            <Typography variant="caption">{fieldLabel(field)}</Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textDecoration: 'line-through', whiteSpace: 'pre-wrap' }}
            >
              Before: {shown(proposal.before[field])}
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              After: {shown(proposal.after[field])}
            </Typography>
          </Stack>
        ))}
        {proposal.built && (
          <Typography variant="caption" color="text.secondary">
            Already built: applying changes the plan only, until it is rebuilt.
          </Typography>
        )}
        {outcome === undefined ? (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              disabled={busy}
              onClick={onApply}
            >
              Apply
            </Button>
            <Button size="small" onClick={onDismiss}>
              Dismiss
            </Button>
          </Stack>
        ) : (
          <Settled
            outcome={outcome}
            applied="Applied. It is in the lesson’s history, where it can be reverted."
          />
        )}
      </Stack>
    </Paper>
  );
}

/** What the planner was given to keep the story distinct, and never more — CURR-41. */
function comparedWith({
  inCourse,
  elsewhere,
}: PlanningStoryProposal['compared']): string {
  if (inCourse + elsewhere === 0)
    return 'No other saved module story was given to the AI, so this one was not compared with any.';
  return `Compared with ${String(inCourse)} other saved ${inCourse === 1 ? 'story' : 'stories'} in this course and ${String(elsewhere)} saved most recently elsewhere, not the whole catalog.`;
}

function Settled({ outcome, applied }: { outcome: Outcome; applied: string }) {
  if (outcome.state === 'refused')
    return (
      <Alert severity="warning">
        {REFUSED[outcome.reason]}. {outcome.message}
      </Alert>
    );
  return (
    <Alert severity={outcome.state === 'applied' ? 'success' : 'info'}>
      {outcome.state === 'applied'
        ? applied
        : 'Dismissed. Nothing was written.'}
    </Alert>
  );
}

/** A module story and one beat per lesson, saved only when accepted — CURR-41. */
export function StoryProposal({
  proposal,
  outcome,
  busy,
  link,
  onAccept,
  onDismiss,
}: {
  proposal: PlanningStoryProposal;
  outcome: Outcome | undefined;
  busy: boolean;
  link: (lessonId: string) => ReactNode;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label={`Proposed story for ${proposal.module}`}
      sx={{ p: 1 }}
    >
      <Stack spacing={0.5}>
        <Typography variant="subtitle2">Story for {proposal.module}</Typography>
        <Typography variant="caption">Module story</Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textDecoration: 'line-through', whiteSpace: 'pre-wrap' }}
        >
          Before: {proposal.before === '' ? 'no story saved' : proposal.before}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          After: {proposal.after}
        </Typography>
        <Typography variant="caption">Case figures</Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textDecoration: 'line-through', whiteSpace: 'pre-wrap' }}
        >
          Before: {shown(proposal.figures.before.join('\n'))}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          After: {shown(proposal.figures.after.join('\n'))}
        </Typography>
        <Typography variant="caption">
          Story beats ({proposal.beats.length})
        </Typography>
        <Stack component="ol" aria-label="Story beats" sx={{ m: 0, pl: 2 }}>
          {proposal.beats.map((beat) => (
            <Typography component="li" variant="body2" key={beat.lessonId}>
              {link(beat.lessonId)} · {beat.name}
              <Typography
                component="span"
                variant="body2"
                color="text.secondary"
                sx={{ display: 'block', textDecoration: 'line-through' }}
              >
                Before: {shown(beat.before)}
              </Typography>
              <Typography
                component="span"
                variant="body2"
                sx={{ display: 'block' }}
              >
                After: {beat.after}
              </Typography>
            </Typography>
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary">
          {comparedWith(proposal.compared)}
        </Typography>
        {outcome === undefined ? (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              disabled={busy}
              onClick={onAccept}
            >
              Accept story
            </Button>
            <Button size="small" onClick={onDismiss}>
              Dismiss
            </Button>
          </Stack>
        ) : (
          <Settled
            outcome={outcome}
            applied="Accepted. The module story and its beats are saved."
          />
        )}
      </Stack>
    </Paper>
  );
}

/**
 * A module's cast and each lesson's subset, saved only when accepted — CURR-44. The saved
 * cast is shown beside it because accepting replaces it whole.
 */
function CastProposal({
  proposal,
  outcome,
  busy,
  link,
  onAccept,
  onDismiss,
}: {
  proposal: PlanningCastProposal;
  outcome: Outcome | undefined;
  busy: boolean;
  link: (lessonId: string) => ReactNode;
  onAccept: () => void;
  onDismiss: () => void;
}) {
  const people = new Map(
    (useQuery(castQuery).data?.cast ?? []).map((one) => [one.id, one]),
  );
  const nameOf = (id: string): string => {
    const one = proposal.after.find((each) => each.id === id);
    return one === undefined ? id : assignmentName(one, people);
  };
  const lead = proposal.after.filter((one) => one.part === 'lead');
  const supporting = proposal.after.filter((one) => one.part !== 'lead');
  const lines = (title: string, cast: typeof proposal.after) =>
    cast.length > 0 && (
      <>
        <Typography variant="caption">{title}</Typography>
        <Stack component="ul" aria-label={title} sx={{ m: 0, pl: 2 }}>
          {cast.map((one) => {
            const entry =
              one.character === undefined
                ? undefined
                : people.get(one.character);
            return (
              <Typography component="li" variant="body2" key={one.id}>
                {castLine(one, people)}
                {entry === undefined
                  ? ''
                  : ` · ${READINESS_LABEL[entry.readiness]}`}
              </Typography>
            );
          })}
        </Stack>
      </>
    );

  return (
    <Paper
      variant="outlined"
      component="section"
      aria-label={`Proposed cast for ${proposal.module}`}
      sx={{ p: 1 }}
    >
      <Stack spacing={0.5}>
        <Typography variant="subtitle2">Cast for {proposal.module}</Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ textDecoration: 'line-through' }}
        >
          Before:{' '}
          {proposal.before.length === 0
            ? 'no cast saved'
            : proposal.before.map((one) => castLine(one, people)).join(', ')}
        </Typography>
        {lines('Lead', lead)}
        {lines('Supporting', supporting)}
        <Typography variant="caption">
          In each lesson ({proposal.lessons.length})
        </Typography>
        <Stack component="ol" aria-label="Cast by lesson" sx={{ m: 0, pl: 2 }}>
          {proposal.lessons.map((one) => (
            <Typography component="li" variant="body2" key={one.lessonId}>
              {link(one.lessonId)} · {one.name}:{' '}
              {one.after.length === 0
                ? 'nobody'
                : one.after.map(nameOf).join(', ')}
            </Typography>
          ))}
        </Stack>
        {outcome === undefined ? (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="contained"
              disabled={busy}
              onClick={onAccept}
            >
              Accept cast
            </Button>
            <Button size="small" onClick={onDismiss}>
              Dismiss
            </Button>
          </Stack>
        ) : (
          <Settled
            outcome={outcome}
            applied="Accepted. The module's cast and each lesson's characters are saved."
          />
        )}
      </Stack>
    </Paper>
  );
}
